import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useCallback, useEffect, useMemo, useState } from "react";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import { yupResolver } from "@hookform/resolvers/yup";
import { useCreateRide, useDeleteRide, useUpdateRide, useRideDetails } from "api/ride";
import CustomReactSelect from "components/CustomReactSelect";
import HeaderCard from "components/DataTable/HeaderCard";
import ImageUploadField from "components/ImageUploadField";
import Loading from "components/Loading";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import StaffSelector from "components/StaffSelector";
import { parentRoute as checklistParentRoute } from "checklistItems";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useGlobalStore from "store/useGlobalStore";
import Swal from "sweetalert2";
import { getStatusFilters, jsonToFormData, removeEmptyFields } from "utils/helper";
import * as yup from "yup";
import { parentRoute } from ".";
import { formatDateTime, STATUS_ACTIVE } from "utils/helper";

const rideSchema = yup.object({
    rd_name: yup.string().trim().required("Ride name is required").max(100, "Max 100 characters"),
    rd_description: yup.string().max(200, "Max 200 characters").nullable(),
    rd_staff: yup.array().of(yup.number()).nullable(),
});

const AddRide = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setPrevSelectedRow } = useGlobalStore();
    const [formData, setFormData] = useState({
        rd_name: "",
        rd_description: "",
        rd_status: "",
        rd_image: null,
    });
    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const statusSelection = getStatusFilters(false)
    const id = Number(location.pathname.split('/')[2]);

    const isAdd = useMemo(() =>
        location.pathname.includes(`${parentRoute}/add-ride`),
        [location.pathname]
    );

    const rideDetailQuery = useRideDetails(id, {
        enabled: !isAdd && !!id && !isNaN(id),
    });

    const createRideMutation = useCreateRide();
    const updateRideMutation = useUpdateRide();
    const deleteRideMutation = useDeleteRide();

    const isDetailLoading = rideDetailQuery.isFetching;
    const checklistSummary = rideDetailQuery.data?.checklist_summary || [];

    useEffect(() => {
        if (isAdd) return;
        if (!id || isNaN(id)) {
            navigate(parentRoute, { state: location.state });
            return;
        }
        if (rideDetailQuery.isError) {
            toast.error("Failed to find ride data");
            navigate(parentRoute, { state: location.state });
        }
    }, [id, isAdd, rideDetailQuery.isError, navigate, location.state]);

    useEffect(() => {
        if (!rideDetailQuery.data) return;
        const details = rideDetailQuery.data;
        setFormData({
            id: details.id,
            rd_name: details.name,
            rd_description: details.description,
            rd_status: details.status?.id,
            rd_image: details.img_url || null,
            rd_staff: details.users.map(user => user.id)
        });
    }, [rideDetailQuery.data]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);


    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes'
        });

        if (result.isConfirmed) {
            try {
                await deleteRideMutation.mutateAsync(formData.id);
                toast.success("Ride deleted successfully");
                navigate(parentRoute);
            } catch (err) {
                toast.error(err?.data?.message || "Failed to delete ride");
            }
        }
    }

    const buildChecklistListState = useCallback((filters = {}) => ({
        sort: "updated_at",
        dir: "desc",
        page: 1,
        search: "",
        status: STATUS_ACTIVE,
        type: "",
        category: "",
        ride_id: formData.id,
        ...filters,
    }), [formData.id]);

    const handleOpenRideChecklists = useCallback((filters = {}) => {
        navigate(checklistParentRoute, {
            state: buildChecklistListState(filters),
        });
    }, [navigate, buildChecklistListState]);

    const handleAddChecklistForRide = useCallback(() => {
        navigate(`${checklistParentRoute}/add-checklist`, {
            state: buildChecklistListState(),
        });
    }, [navigate, buildChecklistListState]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setFormErrors({});

        try {
            let passData = jsonToFormData(removeEmptyFields(formData))

            if (isAdd) {
                await createRideMutation.mutateAsync(passData);
                toast.success("Ride added successfully");
                navigate(parentRoute, { state: location.state });
            } else {
                await updateRideMutation.mutateAsync({ id: formData.id, rideData: passData });
                toast.success("Ride updated successfully");
            }
        } catch (err) {
            setFormErrors(err?.data?.errors || {});
            toast.error(err?.data?.message || (isAdd ? "Failed to create ride" : "Failed to update ride"));
        } finally {
            setIsLoading(false);
        }
    }, [formData, navigate, isAdd, location.state, createRideMutation, updateRideMutation]);

    const {
        trigger,
        setValue,
        formState: { errors: rhfErrors },
    } = useForm({
        resolver: yupResolver(rideSchema),
        mode: "onSubmit",
        defaultValues: {
            rd_name: "",
            rd_description: "",
            rd_status: "",
            rd_image: null,
            rd_staff: [],
        },
    });

    // Sync your formData to RHF for validation
    useEffect(() => {
        Object.entries(formData).forEach(([k, v]) => setValue(k, v, { shouldValidate: false, shouldDirty: false }));
    }, [formData, setValue]);

    // Map RHF errors to your formErrors for UI
    useEffect(() => {
        const mapped = {};
        Object.entries(rhfErrors || {}).forEach(([k, v]) => {
            mapped[k] = v?.message;
        });
        setFormErrors(mapped);
    }, [rhfErrors]);

    const handleValidatedSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setFormErrors({});
            const valid = await trigger();
            if (!valid) {
                toast.error("Please fix the validation errors before submitting.");
                return;
            }
            await handleSubmit(e); // your original submit logic
        },
        [trigger, handleSubmit]
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={4} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <HeaderCard
                                title={isAdd ? "Create a New Ride" : "Edit Ride"}
                                showBackButton={true}
                                onBackClick={() => { navigate(parentRoute, { state: location.state }); setPrevSelectedRow(id, parentRoute) }}
                            />
                            {isDetailLoading && !isAdd ? <Loading /> :
                                <MDBox pt={3} px={2} pb={2}>
                                    {!isAdd && (
                                        <MDBox mb={2}>
                                            <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                                                <MDTypography variant="body2" color="text" gutterBottom>
                                                    Created At: {rideDetailQuery.data?.created_at ? formatDateTime(rideDetailQuery.data.created_at) : '-'}
                                                </MDTypography>
                                                <MDTypography variant="body2" color="text" gutterBottom>
                                                    Last Updated: {rideDetailQuery.data?.updated_at ? formatDateTime(rideDetailQuery.data.updated_at) : '-'}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    )}
                                    <form noValidate onSubmit={handleValidatedSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 'auto' }}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={3} style={{ flexGrow: 1 }}>
                                                <Grid item xs={12} md={isAdd ? 12 : 6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Ride name"
                                                        name="rd_name"
                                                        value={formData?.rd_name || ''}
                                                        helperText={formErrors.rd_name}
                                                        error={Boolean(formErrors.rd_name)}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </Grid>
                                                {!isAdd && <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="Status"
                                                        name="rd_status"
                                                        variant="outlined"
                                                        value={statusSelection.find(status => status.value === formData?.rd_status ?? formData?.rd_status?.id)}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'rd_status', value: selectedOption?.value } })}
                                                        options={statusSelection}
                                                        required
                                                    />
                                                </Grid>}
                                                <Grid item xs={12}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Description"
                                                        name="rd_description"
                                                        helperText={formErrors.rd_description}
                                                        error={Boolean(formErrors.rd_description)}
                                                        value={formData?.rd_description || ''}
                                                        onChange={handleChange}
                                                        multiline
                                                        minRows={2}
                                                        inputProps={{ maxLength: 200 }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <StaffSelector
                                                        label="Responsible Staff"
                                                        placeholder="Search staff..."
                                                        isMulti
                                                        value={formData?.rd_staff || []}
                                                        onChange={options =>
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                rd_staff: options || []
                                                            }))
                                                        }
                                                        required={false}
                                                    />
                                                </Grid>


                                                <Grid item xs={12}>
                                                    <ImageUploadField
                                                        value={formData?.rd_image}
                                                        onChange={(file) => setFormData(prev => ({ ...prev, rd_image: file }))}
                                                        name="rd_image"
                                                        label="Ride Image"
                                                    />
                                                </Grid>
                                                {/* <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="Checklist Items"
                                                        name="rd_checklist_item"
                                                        variant="outlined"
                                                        isMulti
                                                        value={
                                                            formData?.rd_checklist_item
                                                                ? checklistOptions.filter(opt =>
                                                                    formData.rd_checklist_item.includes(opt.value)
                                                                )
                                                                : []
                                                        }
                                                        onChange={selectedOptions =>
                                                            handleChange({
                                                                target: {
                                                                    name: 'rd_checklist_item',
                                                                    value: selectedOptions ? selectedOptions.map(opt => opt.value) : []
                                                                }
                                                            })
                                                        }
                                                        options={checklistOptions}
                                                        placeholder="Select checklist items"
                                                    />
                                                </Grid> */}

                                                <Grid item xs={12}>
                                                    <Grid container>
                                                        <Grid item>
                                                            {!isAdd && <MDButton
                                                                variant="contained"
                                                                color="error"
                                                                onClick={handleDelete}
                                                                loading={isLoading}
                                                            >
                                                                Delete
                                                            </MDButton>}
                                                        </Grid>
                                                        <Grid item xs />
                                                        <Grid item>
                                                            <MDBox display="flex" justifyContent="flex-end">
                                                                <MDButton
                                                                    variant="contained"
                                                                    color="primary"
                                                                    type="submit"
                                                                    loading={isLoading}
                                                                >
                                                                    Submit
                                                                </MDButton>
                                                            </MDBox>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </form>
                                    {!isAdd && (
                                        <MDBox mt={3} p={2} borderRadius="lg" sx={{ border: "1px solid #e3e6ea", backgroundColor: "#fafbfd" }}>
                                            <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                                <MDTypography variant="button" fontWeight="bold">
                                                    Checklist Summary
                                                </MDTypography>
                                                <MDBox display="flex" gap={1} flexWrap="wrap">
                                                    <MDButton
                                                        size="small"
                                                        variant="text"
                                                        color="info"
                                                        onClick={() => handleOpenRideChecklists()}
                                                    >
                                                        View Ride Checklists
                                                    </MDButton>
                                                    <MDButton
                                                        size="small"
                                                        variant="contained"
                                                        color="info"
                                                        onClick={handleAddChecklistForRide}
                                                    >
                                                        Add Checklist Item
                                                    </MDButton>
                                                </MDBox>
                                            </MDBox>

                                            {checklistSummary.length > 0 ? (
                                                <Grid container spacing={1.5} mt={0.5}>
                                                    {checklistSummary.map((summary, index) => (
                                                        <Grid item xs={12} sm={6} md={4} lg={3} key={`${summary?.type?.id || "type"}-${summary?.category?.id || "cat"}-${index}`}>
                                                            <MDBox
                                                                px={1.5}
                                                                py={1.25}
                                                                borderRadius="md"
                                                                sx={{ border: "1px solid #dde3ea", backgroundColor: "#ffffff" }}
                                                            >
                                                                <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                                                    <MDTypography variant="caption" color="dark" fontWeight="bold">
                                                                        {summary?.type?.name || "-"}
                                                                    </MDTypography>
                                                                    <MDTypography variant="button" color="primary" fontWeight="bold">
                                                                        {summary?.checklist_count ?? 0}
                                                                    </MDTypography>
                                                                </MDBox>
                                                                <MDTypography variant="caption" color="text" display="block" mt={0.25}>
                                                                    {summary?.category?.name || "-"}
                                                                </MDTypography>
                                                                <MDButton
                                                                    size="small"
                                                                    variant="text"
                                                                    color="info"
                                                                    sx={{ px: 0, minWidth: "auto", mt: 0.5 }}
                                                                    onClick={() => handleOpenRideChecklists({
                                                                        type: summary?.type?.id,
                                                                        category: summary?.category?.id,
                                                                    })}
                                                                >
                                                                    View Items
                                                                </MDButton>
                                                            </MDBox>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            ) : (
                                                <MDTypography variant="caption" color="text" mt={1.25} display="block">
                                                    No checklist items are linked to this ride yet.
                                                </MDTypography>
                                            )}
                                        </MDBox>
                                    )}
                                </MDBox>}
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
};

export default AddRide;
